/**
 * Penny-drop name-mismatch state for the PennyDrop screen (bureau-only credit path).
 *
 * The SERVER owns the cap: it fails the PENNY_DROP step with remark `NAME_MISMATCH_LIMIT`, and
 * an ops re-open from cpbase resets the counter (remark `PENNY_DROP_REOPENED_REMARK`, the
 * step's `updatedAt` = the reset point). This helper mirrors that rule so the screen never
 * decides "capped" from a raw attempt count — which, after a re-open, would bounce a tenant
 * with a fresh budget straight back to the sorry screen. Dependency-free; see the node:test.
 */
export const PENNY_DROP_MAX_MISMATCHES = 3;
export const PENNY_DROP_NAME_MISMATCH_LIMIT_REMARK = "NAME_MISMATCH_LIMIT";
export const PENNY_DROP_REOPENED_REMARK = "Re-opened by ops after the name-mismatch cap";

/**
 * @returns null when the form should render clean; otherwise
 *          { capped: boolean, attemptsLeft: number } — `capped` only when the SERVER says so.
 */
export function resolvePennyDropMismatchState(attempts, pennyDropStep) {
  const list = Array.isArray(attempts) ? attempts : [];
  const step = pennyDropStep || {};
  if (step.status === "FAILED" && step.remark === PENNY_DROP_NAME_MISMATCH_LIMIT_REMARK) {
    return { capped: true, attemptsLeft: 0 };
  }
  const resetAt =
    step.remark === PENNY_DROP_REOPENED_REMARK && step.updatedAt
      ? new Date(step.updatedAt).getTime()
      : null;
  const counted = list.filter((a) => {
    if (!a?.nameMatch || a.nameMatch.matched !== false) return false;
    if (resetAt === null || !Number.isFinite(resetAt)) return true;
    const t = a.timestamp ? new Date(a.timestamp).getTime() : NaN;
    return !Number.isFinite(t) || t > resetAt;
  });
  const latest = list[list.length - 1];
  const latestCounted = latest && counted.includes(latest);
  if (!latestCounted) return null;
  return {
    capped: false,
    attemptsLeft: Math.max(0, PENNY_DROP_MAX_MISMATCHES - counted.length),
  };
}

/**
 * The legacy client-side lock: 3 recorded attempts and the form is disabled with "Maximum attempts".
 *
 * It applies to with-banking journeys only, where nothing on the server caps penny drop. On the
 * bureau-only path (`creditPath === "WITHOUT_BANKING"`) the SERVER owns the cap — it fails the step
 * with NAME_MISMATCH_LIMIT and an ops re-open resets it — so the raw count on the row must never
 * lock the screen: after a re-open the row still carries the old attempts and the tenant would be
 * locked out of the fresh tries they were just given. `creditPath` absent (older backend) keeps the
 * legacy rule, i.e. today's behaviour.
 */
export function isRawAttemptCapReached(creditPath, attempts) {
  if (creditPath === "WITHOUT_BANKING") return false;
  return Array.isArray(attempts) && attempts.length >= PENNY_DROP_MAX_MISMATCHES;
}
